import { useRef, useState } from "react";
import { Upload, X, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadPhoto } from "@/lib/api";

export default function PhotoUploader({ value = [], onChange, label, testIdPrefix = "photo", min = 0 }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  async function handleFiles(files) {
    if (!files || !files.length) return;
    setUploading(true);
    const arr = Array.from(files);
    const results = [];
    for (const f of arr) {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name} is not an image`);
        continue;
      }
      try {
        const data = await uploadPhoto(f);
        results.push(data);
      } catch (e) {
        console.error(e);
        toast.error(`Failed to upload ${f.name}`);
      }
    }
    onChange([...(value || []), ...results]);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  }

  function removeAt(idx) {
    const next = value.slice();
    next.splice(idx, 1);
    onChange(next);
  }

  return (
    <div data-testid={`${testIdPrefix}-uploader`}>
      {label && <p className="text-sm font-medium text-slate-700 mb-2">{label}</p>}
      <div className="flex flex-wrap gap-3">
        {value.map((p, i) => (
          <div key={p.id} className="relative group h-24 w-24 rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
            <img src={p.url} alt="upload" className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={() => removeAt(i)}
              data-testid={`${testIdPrefix}-remove-${i}`}
              className="absolute top-1 right-1 h-6 w-6 rounded-full bg-slate-900/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Remove photo"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testIdPrefix}-add-btn`}
          className="h-24 w-24 rounded-xl border-2 border-dashed border-[#FFB870] bg-[#FFF7EE] hover:bg-[#FFF3E6] text-[#FF7F00] flex flex-col items-center justify-center gap-1 transition-colors disabled:opacity-60"
        >
          {uploading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : value.length === 0 ? (
            <>
              <Upload className="h-5 w-5" />
              <span className="text-[11px] font-semibold">Upload</span>
            </>
          ) : (
            <>
              <ImagePlus className="h-5 w-5" />
              <span className="text-[11px] font-semibold">Add</span>
            </>
          )}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
        data-testid={`${testIdPrefix}-input`}
      />
      {min > 0 && value.length < min && (
        <p className="mt-2 text-xs text-slate-500">Upload at least {min} photo{min > 1 ? "s" : ""}.</p>
      )}
    </div>
  );
}
